import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { getStoredUser } from '@/hooks/useAuth';
import { OwnerStation } from '@/mocks/ownerDashboard';
import { useOwnerDashboard } from '@/hooks/useOwnerDashboard';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import HeroKpiRow from './components/HeroKpiRow';
import RevenueChart from './components/RevenueChart';
import ForecastAccuracyFleetWidget from './components/ForecastAccuracyFleetWidget';
import MapSection from './components/MapAndRevenue';
import PortfolioTilesSection from './components/PortfolioTilesSection';
import OperationsSection from './components/OperationsSection';
import InverterHealth from './components/InverterHealth';
import SecondaryAndFinancial from './components/SecondaryAndFinancial';

export default function OwnerDashboardPage() {
  const { isDark } = useTheme();
  const { t, i18n } = useTranslation();
  const user = getStoredUser();
  const navigate = useNavigate();
  const { setSelectedAsset } = useSelectedAsset();
  const { stations, incidents, kpis, lostRevenue, loading, isFallback } = useOwnerDashboard();

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';

  const _dateLocale = (i18n.language || 'uk').toLowerCase().startsWith('pl') ? 'pl-PL' : (i18n.language || 'uk').toLowerCase().startsWith('en') ? 'en-US' : 'uk-UA';
  const _today = new Date().toLocaleDateString(_dateLocale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const todayLabel = _today.charAt(0).toUpperCase() + _today.slice(1);

  // Time-of-day greeting for the owner. Vocative only for the safe -ій ending
  // (Юрій→Юрію, Андрій→Андрію); other names stay nominative to avoid wrong forms.
  const _hour = new Date().getHours();
  const greeting =
    _hour >= 5 && _hour < 12 ? t('greeting_morning')
    : _hour >= 12 && _hour < 18 ? t('greeting_day')
    : _hour >= 18 && _hour < 23 ? t('greeting_evening')
    : t('greeting_night');
  const _firstName = (user?.displayName ?? '').split(' ')[0];
  const ownerName = /ій$/.test(_firstName) ? _firstName.slice(0, -1) + 'ю' : _firstName;

  // Sync the global asset selector (topbar / forecast page) and route to a REAL
  // app page — never the invented Readdy drill-down modal.
  const selectAsset = (s: OwnerStation) =>
    setSelectedAsset({
      id: s.id,
      assetName: s.name,
      installedCapacityMw: s.installedMw,
      region: s.region,
      operationalStatus: s.status,
    });
  const openAssetDetail = (s: OwnerStation) => { selectAsset(s); navigate(`/assets/${s.id}`); };
  const openAssetTechnical = (s: OwnerStation) => { selectAsset(s); navigate(`/assets/${s.id}?tab=technical`); };
  const openAssetFinancial = (s: OwnerStation) => { selectAsset(s); navigate(`/assets/${s.id}?tab=financial`); };
  const openAssetForecast = (s: OwnerStation) => { selectAsset(s); navigate('/forecast'); };

  return (
    <DashboardLayout breadcrumb={['Дашборд']}>
      <div className="max-w-full" style={{ color: textMain }}>
        {/* Page header — owner greeting (replaces the removed topbar station selector) */}
        <div className="mb-5">
          <h1
            className="text-xl font-bold mb-1"
            style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}
          >
            {greeting}{ownerName ? `, ${ownerName}` : ''} 👋
          </h1>
          <p
            className="text-sm"
            style={{ color: textMuted, fontFamily: 'Inter, sans-serif' }}
          >
            <span style={{ color: textMain, fontWeight: 600 }}>{todayLabel}</span>
            {isFallback && (
              <span style={{ color: '#F59E0B' }}> · {t('d_demo_data')}</span>
            )}
          </p>
        </div>

        {/* Section 1: Hero KPI Row */}
        <HeroKpiRow
          kpis={kpis}
          stations={stations}
          loading={loading}
          onStationFinancial={openAssetFinancial}
          onStationDetail={openAssetTechnical}
          onStationForecast={openAssetForecast}
        />

        {/* Section 2: Revenue Chart — full width */}
        <RevenueChart />

        {/* Section 2.5: Portfolio forecast accuracy (clean nMAE) */}
        <ForecastAccuracyFleetWidget />

        {/* Section 3: Map + Portfolio Tiles side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-4 mb-5">
          <MapSection
            stations={stations}
            onStationClick={openAssetDetail}
            onStationForecast={openAssetForecast}
          />
          <PortfolioTilesSection stations={stations} onStationClick={openAssetDetail} compact />
        </div>

        {/* Section 4: Operations */}
        <OperationsSection incidents={incidents} lostRevenue={lostRevenue} />

        {/* Section 4.5: Inverter health (Ф4) */}
        <InverterHealth />

        {/* Section 5 + 6: Secondary + Financial */}
        <SecondaryAndFinancial />
      </div>
    </DashboardLayout>
  );
}